import React, { useEffect, useState, useCallback, useMemo } from 'react';
// ...existing imports...
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardHeader from '../components/DashboardHeader';
import './CheckoutPage.css'; // <-- Add this

const CheckoutPage = () => {
  // User points state
  const [userPoints, setUserPoints] = useState(0);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showGreenBar, setShowGreenBar] = useState(true);
  
  // Handle responsive breakpoint
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Hide green bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowGreenBar(window.scrollY < 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Promo code logic
  const [promoCode, setPromoCode] = useState(location.state?.promoCode || '');
  const [promoError, setPromoError] = useState('');
  const [discount, setDiscount] = useState(location.state?.discount || 0);
  const [discountType, setDiscountType] = useState(location.state?.discountType || '%');
  const [promoApplied, setPromoApplied] = useState(location.state?.promoApplied || false);
  const [voucherId, setVoucherId] = useState(location.state?.voucherId || null);
  
  // Promotions (admin) shown on checkout
  const [availablePromotions, setAvailablePromotions] = useState([]);

  // Admin reward system
  const [availableRewards, setAvailableRewards] = useState([]);
  const [selectedReward, setSelectedReward] = useState(null);
  const [rewardError, setRewardError] = useState('');

  const handleApplyPromo = async (codeOverride) => {
    setPromoError('');
    setPromoApplied(false);

    const rawCode = typeof codeOverride === 'string' ? codeOverride : promoCode;
    const code = (rawCode ?? '').toString().trim();
    if (!code) {
      setPromoError('Please enter a promo code.');
      return;
    }

    try {
      // Use the promotion service helper
      const res = await axios.get(`${process.env.REACT_APP_API}/api/promotions/${code}` , {
        headers: { Authorization: `Bearer ${token}` }
      });
      const promo = res.data;

      if (!promo) {
        setPromoError('Invalid promo code.');
        setDiscount(0);
        return;
      }

      // Check active
      if (!promo.active) {
        setPromoError('This promo code is no longer active.');
        setDiscount(0);
        return;
      }

      // Date validation
      const today = new Date();
      const validFrom = promo.validFrom ? new Date(promo.validFrom) : null;
      const validTo = promo.validTo ? new Date(promo.validTo) : null;
      if ((validFrom && today < validFrom) || (validTo && today > validTo)) {
        setPromoError('This promo code has expired or is not yet valid.');
        setDiscount(0);
        return;
      }

      // Min cart validation
      if (promo.minCartAmount && subtotal < promo.minCartAmount) {
        setPromoError(`Minimum cart value for this promo is ₱${promo.minCartAmount}`);
        setDiscount(0);
        return;
      }

      // Client: do not enforce global 'uses' here (backend enforces per-user limits).
      // If needed, a client-side check against promo.redeemedBy could be added.

      // All good: apply values from backend schema (value / discountType)
      setDiscount(Number(promo.value || promo.amount || 0));
      setDiscountType(promo.discountType || promo.type || '%');
      setPromoApplied(true);
      // reflect the applied code in the input field
      setPromoCode(promo.code || code);
    } catch (err) {
      console.error('Error applying promo:', err);
      // If axios returned 404, give friendly message
      if (err.response && err.response.status === 404) {
        setPromoError('Invalid promo code.');
      } else {
        setPromoError('Error validating promo code. Please try again.');
      }
      setDiscount(0);
    }
  };
  const initialCart = useMemo(() => location.state?.cart || [], [location.state]);

  const [cart, setCart] = useState(initialCart);
  const [subtotal, setSubtotal] = useState(
    initialCart.length > 0
      ? initialCart.reduce((acc, item) => {
          if (item.product && item.product.price) {
            const hasDiscount = item.product.discount && Number(item.product.discount) > 0;
            const price = hasDiscount
              ? (Number(item.product.price) - (Number(item.product.price) * (Number(item.product.discount) / 100)))
              : Number(item.product.price);
            return acc + price * item.quantity;
          }
          return acc;
        }, 0)
      : 0
  );
  const [formData, setFormData] = useState({
    region: '',
    firstName: '',
    lastName: '',
    email: '',
    fullAddress: '',
    postalCode: '',
    city: '',
    phoneNumber: '',
    landmark: '',
    deliveryNotes: '',
    paymentMethod: 'online',
  });
  const [checkoutErrors, setCheckoutErrors] = useState({});

  // Dynamic provinces map fetched from backend and merged with defaults
  // Provinces are sourced from Delivery Zones to avoid drift and duplicates
  const [provincesMap, setProvincesMap] = useState({});
  const [loadingProvinces, setLoadingProvinces] = useState(true);

  // Fetch provinces from backend and merge with defaults, de-duplicating case-insensitively
  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        // Use delivery zones-derived provinces (only zones that have availability configured)
        const res = await axios.get(`${process.env.REACT_APP_API}/api/delivery/public-provinces`);
        const list = Array.isArray(res.data) ? res.data : [];

        // Build a map from fetched list only (no local defaults to prevent duplicates)
        const fromDb = {};
        for (const p of list) {
          if (!p || !p.name) continue;
          const name = p.name.toString().trim();
          const cities = Array.isArray(p.cities) ? p.cities.filter(Boolean) : [];
          fromDb[name] = Array.from(new Set(cities));
        }
        const result = {};
        Object.keys(fromDb).forEach((name) => {
          result[name] = Array.from(new Set(fromDb[name] || [])).sort((a, b) => a.localeCompare(b));
        });

        // Sort provinces alphabetically for display
        const sorted = Object.fromEntries(
          Object.keys(result)
            .sort((a, b) => a.localeCompare(b))
            .map((k) => [k, result[k]])
        );

        setProvincesMap(sorted);
      } catch (err) {
        console.error('Error fetching provinces', err);
        // Fallback to empty map if API fails
        setProvincesMap({});
      } finally {
        setLoadingProvinces(false);
      }
    };
    fetchProvinces();
  }, []);

  // Helper: normalize province strings to Title Case so they match PROVINCES keys
  // Find the canonical PROVINCES key for a given input (case-insensitive, tolerant)
  const findProvinceKey = useCallback((input) => {
    if (!input) return null;
    const normalized = input.toString().trim().toLowerCase();
    const keys = Object.keys(provincesMap || {});
    const exact = keys.find(k => k.toLowerCase() === normalized);
    if (exact) return exact;
    // Fallback: match by normalized (diacritics removed) inclusion
    const norm = s => s.toString().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
    const n = norm(normalized);
    return keys.find(k => {
      const kk = norm(k);
      return kk.includes(n) || n.includes(kk);
    }) || null;
  }, [provincesMap]);

  // Find province key by city name
  const findProvinceByCity = useCallback((city) => {
    if (!city) return null;
    const norm = s => s.toString().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
    const ncity = norm(city);
    const keys = Object.keys(provincesMap || {});
    for (const k of keys) {
      const cities = provincesMap[k] || [];
      for (const c of cities) {
        if (norm(c) === ncity) return k;
      }
    }
    return null;
  }, [provincesMap]);

  const [deliveryFeeInfo, setDeliveryFeeInfo] = useState({ available: true, fee: 0 });
  const [shippingMethod, setShippingMethod] = useState('standard');

  // Compute delivery fee based on backend-configured zone and optional delivery rewards
  const deliveryFee = (() => {
    // Store pickup is always free
    if (shippingMethod === 'pickup') return 0;
    // if subtotal qualifies for free shipping
    if (subtotal >= 10000) return 0;
    const base = (deliveryFeeInfo && deliveryFeeInfo.available) ? (deliveryFeeInfo.fee || 0) : 0;
    if (selectedReward && selectedReward.type === 'delivery' && selectedReward.active) {
      const today = new Date();
      const exp = selectedReward.expirationDate ? new Date(selectedReward.expirationDate) : null;
      if ((!selectedReward.minCartAmount || subtotal >= selectedReward.minCartAmount) && (!exp || exp >= today)) {
        if (selectedReward.valueType === 'flat') {
          return Math.max(base - selectedReward.value, 0);
        } else if (selectedReward.valueType === 'percent') {
          return Math.max(base * (1 - selectedReward.value / 100), 0);
        }
      }
    }
    return base || 0;
  })();
  const token = localStorage.getItem('token');

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [useNewAddress, setUseNewAddress] = useState(true);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);

  // Memoize sorted addresses to prevent re-sorting on every render
  const sortedAddresses = useMemo(() => {
    return [...savedAddresses].sort((a, b) => {
      // Sort by sortOrder if available, otherwise by isProfileAddress
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }
      return (a.isProfileAddress ? -1 : b.isProfileAddress ? 1 : 0);
    });
  }, [savedAddresses]);

  // (vouchers removed) — promotions are fetched separately below

  // Fetch active promotions created by admin (used as promo vouchers)
  const fetchPromotions = useCallback(async () => {
    try {
      // Use the new public endpoint so customers can see active, date-valid promos
      const res = await axios.get(`${process.env.REACT_APP_API}/api/promotions/public`);
      setAvailablePromotions(res.data || []);
    } catch (err) {
      console.error('Error fetching promotions:', err);
      setAvailablePromotions([]);
    }
  }, []);

  // Fetch admin rewards (stable reference)
  const fetchRewards = useCallback(async () => {
    try {
      // Use public rewards endpoint so checkout can display them even for unauthenticated users
      const res = await axios.get(`${process.env.REACT_APP_API}/api/rewards/public`);
      setAvailableRewards(res.data);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      setAvailableRewards([]);
    }
  }, []);

  // Fetch delivery fee for province + city
  const fetchDeliveryFee = useCallback(async (province, city) => {
    try {
      if (!province || !city) return setDeliveryFeeInfo({ available: false, fee: null });
      const res = await axios.get(`${process.env.REACT_APP_API}/api/delivery/fee`, { params: { province, city } });
      setDeliveryFeeInfo({ available: !!res.data.available, fee: res.data.fee ?? 0 });
    } catch (err) {
      console.error('Error fetching delivery fee', err);
      setDeliveryFeeInfo({ available: false, fee: null });
    }
  }, []);

  // Move handleRewardSelect outside useEffect
  const handleRewardSelect = (reward) => {
    setRewardError('');
    if (!reward.active) {
      setRewardError('This reward is not active.');
      return;
    }
    const today = new Date();
    const exp = reward.expirationDate ? new Date(reward.expirationDate) : null;
    if (exp && exp < today) {
      setRewardError('This reward has expired.');
      return;
    }
    if (reward.minCartAmount && subtotal < reward.minCartAmount) {
      setRewardError(`Minimum cart amount for this reward is ₱${reward.minCartAmount}`);
      return;
    }
    setSelectedReward(reward);
  };

  useEffect(() => {
    // Fetch initial data: user points, profile, cart (if needed), rewards & promotions
    const fetchUserPoints = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/api/users/rewards`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserPoints(res.data.points || 0);
      } catch (err) {
        setUserPoints(0);
      }
    };
    fetchUserPoints();

    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/api/users/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data;
        // Store user email for use as fallback
        const userEmail = data.email || '';
        
        if (data.addresses && data.addresses.length > 0) {
          const defaultProfileAddress = {
            _id: 'profile-default',
            label: 'Profile Address',
            isDefault: true,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: userEmail,
            fullAddress: data.addresses[0] || '',
            apartment: data.barangay || data.apartment || '',
            phone: data.contactNumbers?.[0] || '',
            city: data.city || '',
            province: data.province || '',
            postalCode: data.postalCode || '',
            isProfileAddress: true,
            sortOrder: 0 // Ensure profile address always first
          };
          const allAddresses = [defaultProfileAddress, ...(data.savedAddresses || []).map((addr, idx) => ({
            ...addr,
            sortOrder: idx + 1 // Stable sort order for saved addresses
          }))];
          // Add user email as fallback to all saved addresses that don't have one
          const addressesWithEmail = allAddresses.map(addr => ({
            ...addr,
            email: addr.email || userEmail
          }));
          setSavedAddresses(addressesWithEmail);
          setFormData(prev => ({
            ...prev,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phoneNumber: data.contactNumbers?.[0] || '',
            fullAddress: data.addresses[0] || '',
            address2: data.barangay || data.apartment || '',
            city: data.city || '',
            postalCode: data.postalCode || '',
            region: data.province || ''
          }));
          setUseNewAddress(false);
        } else {
          setFormData(prev => ({
            ...prev,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phoneNumber: data.contactNumbers?.[0] || '',
          }));
          setSavedAddresses(data.savedAddresses || []);
        }
      } catch (err) {
        console.error('Error fetching profile', err);
      }
    };

    const fetchCartIfEmpty = async () => {
      if (initialCart.length === 0) {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API}/api/cart`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const items = res.data.items || [];
          setCart(items);
          const sum = items.reduce((acc, item) => {
            if (item.product && item.product.price) {
              const hasDiscount = item.product.discount && Number(item.product.discount) > 0;
              const price = hasDiscount
                ? (Number(item.product.price) - (Number(item.product.price) * (Number(item.product.discount) / 100)))
                : Number(item.product.price);
              return acc + price * item.quantity;
            }
            return acc;
          }, 0);
          setSubtotal(sum);
        } catch (err) {
          console.error('Error fetching cart', err);
        }
      }
    };

    fetchCartIfEmpty();
    fetchProfile();
    fetchRewards();
    fetchPromotions();
  }, [initialCart, token, fetchRewards, fetchPromotions]);

  // Once provinces are loaded, backfill region/city if missing using profile data or sensible defaults
  useEffect(() => {
    if (loadingProvinces) return;
    setFormData(prev => {
      let region = prev.region;
      let city = prev.city;
      // If profile provided raw province, map to canonical key
      if (region) {
        const mapped = findProvinceKey(region) || region;
        region = mapped;
      }
      // If city present but no region, try to find via city
      if (!region && city) {
        const fromCity = findProvinceByCity(city);
        if (fromCity) region = fromCity;
      }
      // If still missing, set first available province and its first city
      if (!region) {
        const keys = Object.keys(provincesMap);
        if (keys.length > 0) {
          region = keys[0];
        }
      }
      if (!city && region) {
        const firstCity = (provincesMap[region] || [])[0] || '';
        city = firstCity;
      }
      if (region !== prev.region || city !== prev.city) {
        // Trigger delivery fee lookup when we set defaults
        if (region && city) fetchDeliveryFee(region, city);
        return { ...prev, region, city };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProvinces, provincesMap, findProvinceKey, findProvinceByCity]);

  // Separate effect: compute delivery fee whenever region or city changes
  useEffect(() => {
    // only attempt lookup when both region and city are selected
    if (!formData.region || !formData.city) return;
    fetchDeliveryFee(formData.region, formData.city);
  }, [formData.region, formData.city, fetchDeliveryFee]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'region') {
      // normalize province selection to our keys
      const prov = value;
      const cities = provincesMap[prov] || [];
      const firstCity = cities[0] || '';
      setFormData(prev => ({ ...prev, city: firstCity }));
      fetchDeliveryFee(prov, firstCity);
    }
    if (name === 'city') {
      fetchDeliveryFee(formData.region, value);
    }
  };

  const validateDelivery = () => {
    const errors = {};
    // Always require contact basics
    if (!formData.firstName || formData.firstName.trim() === '') errors.firstName = 'First name is required.';
    if (!formData.lastName || formData.lastName.trim() === '') errors.lastName = 'Last name is required.';
    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address.';
    }

    // For store pickup, skip address validation entirely
    const isPickup = shippingMethod === 'pickup';
    if (!isPickup) {
      if (!formData.fullAddress || formData.fullAddress.trim().length < 5) errors.fullAddress = 'Please enter a full address (Block and Lot#).';
      if (!formData.address2 || formData.address2.trim() === '') errors.address2 = 'Barangay / Apartment is required.';
      if (!formData.city || formData.city.trim() === '') errors.city = 'City is required.';
      if (!formData.postalCode || !/^\d{4}$/.test(formData.postalCode.trim())) errors.postalCode = 'Postal code must be 4 digits.';
      if (!formData.region || formData.region.trim() === '') errors.region = 'Province / Region is required.';
      // Check delivery availability - only for standard delivery
      if (shippingMethod === 'standard' && !deliveryFeeInfo.available) {
        errors.delivery = 'Delivery is not available for the selected province/city.';
      }
    }
    
    if (formData.phoneNumber && formData.phoneNumber.trim() !== '' && !/^(?:\+63|0)9\d{9}$/.test(formData.phoneNumber.trim())) {
      errors.phoneNumber = 'Phone must be a valid Philippine mobile number (e.g. 0927xxxxxxx).';
    }
    if (!formData.phoneNumber || formData.phoneNumber.trim() === '') {
      errors.phoneNumber = 'Phone number is required.';
    }
    setCheckoutErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // (voucher selection removed)

  const handlePayNow = async () => {
    // Validate delivery details first
    if (!validateDelivery()) {
      // Show alert with specific error
      const errorMessages = Object.values(checkoutErrors).join('\n');
      alert('Please fix the following errors:\n\n' + errorMessages);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      // Save address to profile if checkbox is checked
      if (saveAsDefault && useNewAddress) {
        try {
          await axios.post(
            `${process.env.REACT_APP_API}/api/users/addresses`,
            {
              label: 'Checkout Address',
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              fullAddress: formData.fullAddress,
              apartment: formData.address2 || '',
              city: formData.city,
              province: formData.region,
              postalCode: formData.postalCode,
              phone: formData.phoneNumber,
              isDefault: true
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (saveErr) {
          console.error('Error saving address:', saveErr);
          // Don't block checkout if saving fails
        }
      }

      // If a reward is selected, redeem it first
      let usedVoucherId = voucherId || null;
      if (selectedReward) {
        const redeemRes = await axios.post(
          `${process.env.REACT_APP_API}/api/users/rewards/redeem`,
          { rewardId: selectedReward._id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Update user points after redemption
        setUserPoints(redeemRes.data.remainingPoints || 0);
        // capture voucherId so we can attach it to the order
        if (redeemRes.data.voucherId) {
          setVoucherId(redeemRes.data.voucherId);
          usedVoucherId = redeemRes.data.voucherId; // use local id immediately to avoid state update race
        }
      }

      // Place order via backend
      const deliveryDetails = {
        region: formData.region,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        fullAddress: formData.fullAddress,
        postalCode: formData.postalCode,
        city: formData.city,
        phoneNumber: formData.phoneNumber,
        landmark: formData.landmark,
        deliveryNotes: formData.deliveryNotes,
      };
      const paymentMethod = formData.paymentMethod;

      // Calculate total discount (promo + reward)
      let totalDiscount = 0;
      let totalDiscountType = '';
      
      // Apply promo discount
      if (promoApplied && discount > 0) {
        totalDiscount = discount;
        totalDiscountType = discountType; // Preserve the original type ('%', 'amount', 'fixed')
      }

      // Add admin reward (cart type)
      if (selectedReward && selectedReward.type === 'cart' && selectedReward.active) {
        const today = new Date();
        const exp = selectedReward.expirationDate ? new Date(selectedReward.expirationDate) : null;
        if ((!selectedReward.minCartAmount || subtotal >= selectedReward.minCartAmount) && (!exp || exp >= today)) {
          if (selectedReward.valueType === 'flat') {
            // If we already have a percentage discount, convert everything to flat amount
            if (totalDiscountType === '%') {
              totalDiscount = (subtotal * totalDiscount) / 100 + selectedReward.value;
              totalDiscountType = 'amount';
            } else {
              totalDiscount += selectedReward.value;
              if (!totalDiscountType) totalDiscountType = 'amount';
            }
          } else if (selectedReward.valueType === 'percent') {
            // If mixing percentages, convert to flat amount
            if (totalDiscountType === '%') {
              totalDiscount = (subtotal * totalDiscount) / 100 + (subtotal * selectedReward.value) / 100;
              totalDiscountType = 'amount';
            } else {
              totalDiscount += (subtotal * selectedReward.value) / 100;
              if (!totalDiscountType) totalDiscountType = 'amount';
            }
          }
        }
      }

      // Calculate final amount
      let finalAmount = subtotal;
      if (promoApplied) {
        finalAmount -= (discountType === '%' ? (subtotal * discount) / 100 : discount);
      }
      if (selectedReward && selectedReward.type === 'cart' && selectedReward.active) {
        const today = new Date();
        const exp = selectedReward.expirationDate ? new Date(selectedReward.expirationDate) : null;
        if ((!selectedReward.minCartAmount || subtotal >= selectedReward.minCartAmount) && (!exp || exp >= today)) {
          if (selectedReward.valueType === 'flat') {
            finalAmount -= selectedReward.value;
          } else if (selectedReward.valueType === 'percent') {
            finalAmount -= (subtotal * selectedReward.value) / 100;
          }
        }
      }
      finalAmount += deliveryFee;

      // Handle PayMongo Online payment (GCash, Credit/Debit, PayMaya, etc.)
      if (paymentMethod === 'online') {
        const paymentRes = await axios.post(
          `${process.env.REACT_APP_API}/api/payments/create-payment-link`,
          {
            amount: Math.round(finalAmount * 100), // Convert to centavos
            deliveryDetails,
            discount: totalDiscount,
            discountType: totalDiscountType,
            promoCode: promoApplied ? promoCode : '',
            voucherId: usedVoucherId || null,
            rewardId: selectedReward ? selectedReward._id : null,
            deliveryFee: deliveryFee,
            shippingMethod: shippingMethod,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const checkoutUrl = paymentRes.data.checkoutUrl;
        if (checkoutUrl) {
          // Open PayMongo link in new tab
          window.open(checkoutUrl, '_blank');
          // Navigate current tab to orders page
          window.location.href = '/orders';
        } else {
          alert('Failed to create payment link');
        }
        return;
      }

      // Handle COD
      await axios.post(
        `${process.env.REACT_APP_API}/api/orders/checkout`,
        {
          deliveryDetails,
          paymentMethod,
          discount: totalDiscount,
          discountType: totalDiscountType,
          promoCode: promoApplied ? promoCode : '',
          voucherId: usedVoucherId || null,
          rewardId: selectedReward ? selectedReward._id : null,
          deliveryFee: deliveryFee,
          shippingMethod: shippingMethod,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Order placed successfully!');
      window.location.href = '/orders';
    } catch (err) {
      console.error('placeOrder error', err, err.response);
      const serverMessage = err.response?.data?.message || err.message || 'Failed to place order';
      alert(`Failed to place order: ${serverMessage}`);
    }
  };

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) - only visible at top */}
      {showGreenBar && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, left: 0, zIndex: 9999, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}
        >
          {/* Empty green bar for accent */}
        </motion.div>
      )}
      {/* Navbar always sticky at top, with margin if green bar is visible */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
        style={{ 
          width: '100%', 
          background: 'rgba(255, 255, 255, 0.98)', 
          backdropFilter: 'blur(10px)', 
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)', 
          position: showGreenBar ? 'sticky' : 'fixed',
          top: 0, 
          zIndex: 9998,
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          marginTop: showGreenBar ? '32px' : '0'
        }}
      >
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </motion.div>
      
      <div style={{ width: '100%', maxWidth: '1400px', margin: isMobile ? '20px auto' : '40px auto', padding: isMobile ? '0 16px' : '0 24px', flex: 1 }}>
        <div className="checkout-page">
          {/* LEFT COLUMN */}
          <div className="checkout-left">
            {/* Contact Section */}
            <h2 style={{ marginBottom: '0.7rem' }}>Contact</h2>
            <div>
              <input
             type="email"
             name="email"
             id="checkout-email"
             placeholder="Email"
             value={formData.email}
             onChange={handleChange}
             autoComplete="email"
             required
              />
              {checkoutErrors.email && <div className="field-error">{checkoutErrors.email}</div>}
            </div>

            <div>
              {checkoutErrors.phoneNumber && <div className="field-error">{checkoutErrors.phoneNumber}</div>}
              <input
                type="text"
                name="phoneNumber"
                id="checkout-phoneNumber"
                placeholder="Phone"
                value={formData.phoneNumber}
                onChange={handleChange}
                autoComplete="tel"
              />
            </div>

            {/* Delivery Section */}
            <h2 style={{ margin: '2rem 0 1rem 0' }}>Delivery</h2>

            {/* Shipping Method Section - Moved before address */}
            <h3 style={{ margin: '0 0 0.7rem 0', fontSize: '1.1rem', fontWeight: 600 }}>Shipping Method</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '1.25rem 1rem', 
                border: shippingMethod === 'standard' ? '2px solid #7ba93a' : '2px solid #e0e0e0', 
                borderRadius: '12px', 
                cursor: 'pointer',
                background: shippingMethod === 'standard' ? '#f0f7ed' : 'white',
                transition: 'all 0.3s ease',
                boxShadow: shippingMethod === 'standard' ? '0 4px 12px rgba(123,169,58,0.15)' : '0 2px 6px rgba(0,0,0,0.05)',
                transform: shippingMethod === 'standard' ? 'translateY(-2px)' : 'none'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  border: shippingMethod === 'standard' ? '2px solid #7ba93a' : '2px solid #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '1rem',
                  background: shippingMethod === 'standard' ? '#7ba93a' : 'white',
                  transition: 'all 0.3s ease'
                }}>
                  {shippingMethod === 'standard' && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'white' }} />}
                </div>
                <input 
                  type="radio" 
                  name="shippingMethod" 
                  value="standard" 
                  checked={shippingMethod === 'standard'}
                  onChange={(e) => setShippingMethod(e.target.value)}
                  style={{ position: 'absolute', opacity: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '1.05rem', color: shippingMethod === 'standard' ? '#175c36' : '#333' }}>
                    🚚 Standard Delivery
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>3-5 business days</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#175c36' }}>
                  {deliveryFeeInfo.fee > 0 ? `₱${deliveryFeeInfo.fee}` : 'FREE'}
                </div>
              </label>

              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '1.25rem 1rem', 
                border: shippingMethod === 'pickup' ? '2px solid #7ba93a' : '2px solid #e0e0e0', 
                borderRadius: '12px', 
                cursor: 'pointer',
                background: shippingMethod === 'pickup' ? '#f0f7ed' : 'white',
                transition: 'all 0.3s ease',
                boxShadow: shippingMethod === 'pickup' ? '0 4px 12px rgba(123,169,58,0.15)' : '0 2px 6px rgba(0,0,0,0.05)',
                transform: shippingMethod === 'pickup' ? 'translateY(-2px)' : 'none'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  border: shippingMethod === 'pickup' ? '2px solid #7ba93a' : '2px solid #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '1rem',
                  background: shippingMethod === 'pickup' ? '#7ba93a' : 'white',
                  transition: 'all 0.3s ease'
                }}>
                  {shippingMethod === 'pickup' && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'white' }} />}
                </div>
                <input 
                  type="radio" 
                  name="shippingMethod" 
                  value="pickup" 
                  checked={shippingMethod === 'pickup'}
                  onChange={(e) => setShippingMethod(e.target.value)}
                  style={{ position: 'absolute', opacity: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '1.05rem', color: shippingMethod === 'pickup' ? '#175c36' : '#333' }}>
                    🏪 Store Pickup
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>Pick up from our store</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#175c36' }}>FREE</div>
              </label>

              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '1.25rem 1rem', 
                border: '2px solid #e0e0e0', 
                borderRadius: '12px', 
                cursor: 'not-allowed',
                background: '#f9f9f9',
                opacity: 0.5,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  border: '2px solid #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '1rem',
                  background: 'white'
                }}>
                </div>
                <input 
                  type="radio" 
                  name="shippingMethod" 
                  value="lalamove" 
                  disabled
                  style={{ position: 'absolute', opacity: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '1.05rem', color: '#888' }}>
                    🏍️ Lalamove
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>Same-day delivery (Coming Soon)</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#888' }}>Coming Soon</div>
              </label>
            </div>

            {/* Delivery Address Section */}
            <h3 style={{ margin: '0 0 0.7rem 0', fontSize: '1.1rem', fontWeight: 600 }}>Delivery Address</h3>
            
            {savedAddresses.length > 0 && shippingMethod !== 'pickup' && (
              <div className="saved-addresses-section">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#175c36' }}>📍</span>
                  Your Saved Addresses
                </h3>
                <div className="saved-addresses-grid">
                  {sortedAddresses.map((addr) => (
                    <button
                      key={addr._id}
                      type="button"
                      className={`saved-address-option ${selectedAddressId === addr._id ? 'selected' : ''}`}
                      data-profile={addr.isProfileAddress}
                      onClick={() => {
                        setSelectedAddressId(addr._id);
                        // resolve canonical province & city
                        const provKey = addr.province ? (findProvinceKey(addr.province) || null) : (addr.city ? findProvinceByCity(addr.city) : null);
                        const provinceForForm = provKey || Object.keys(provincesMap)[0] || '';
                        const cityForForm = addr.city || ((provincesMap[provinceForForm] || [])[0] || '');
                        setFormData(prev => ({
                          ...prev,
                          firstName: addr.firstName,
                          lastName: addr.lastName,
                          email: addr.email || '',
                          fullAddress: addr.fullAddress,
                          address2: addr.apartment || addr.address2 || addr.barangay || '',
                          city: cityForForm,
                          region: provinceForForm,
                          postalCode: addr.postalCode,
                          phoneNumber: addr.phone || addr.contactNumber || addr.phoneNumber || '',
                          landmark: addr.landmark || '',
                          deliveryNotes: addr.deliveryNotes || ''
                        }));
                        setUseNewAddress(false);
                        fetchDeliveryFee(provinceForForm, cityForForm);
                      }}
                    >
                      <h4>
                        {addr.isProfileAddress ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: '#7ba93a' }}>👤</span> 
                            Profile Address
                          </span>
                        ) : addr.label || 'Saved Address'}
                      </h4>
                      <p><strong>{addr.firstName} {addr.lastName}</strong></p>
                      <p>{addr.fullAddress}</p>
                      {addr.apartment && <p>{addr.apartment}</p>}
                      {(!addr.apartment && addr.barangay) && <p>{addr.barangay}</p>}
                      <p>{addr.city}, {addr.province} {addr.postalCode}</p>
                      <p>📞 {addr.phone}</p>
                      {(addr.isDefault || addr.isProfileAddress) && (
                        <span className="default-chip" style={addr.isProfileAddress ? { background: '#175c36' } : undefined}>
                          {addr.isProfileAddress ? 'Profile Default' : 'Default'}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="use-new-address-btn"
                  onClick={() => {
                    setUseNewAddress(true);
                    setSelectedAddressId(null);
                  }}
                >
                  + Use a different address
                </button>
              </div>
            )}

            {(shippingMethod !== 'pickup' && (useNewAddress || savedAddresses.length === 0)) && (
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  {checkoutErrors.firstName && <div className="field-error">{checkoutErrors.firstName}</div>}
                  <input
                    type="text"
                    name="firstName"
                    id="checkout-firstName"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleChange}
                    autoComplete="given-name"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  {checkoutErrors.lastName && <div className="field-error">{checkoutErrors.lastName}</div>}
                  <input
                    type="text"
                    name="lastName"
                    id="checkout-lastName"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={handleChange}
                    autoComplete="family-name"
                  />
                </div>
              </div>
            )}
            {shippingMethod !== 'pickup' && (
            <div>
              {checkoutErrors.fullAddress && <div className="field-error">{checkoutErrors.fullAddress}</div>}
              <input
                type="text"
                name="fullAddress"
                id="checkout-fullAddress"
                placeholder="IMPORTANT: Please input FULL Address (Block and Lot#)"
                value={formData.fullAddress}
                onChange={handleChange}
                autoComplete="address-line1"
              />
            </div>
            )}
            {shippingMethod !== 'pickup' && (
            <div>
              {checkoutErrors.address2 && <div className="field-error">{checkoutErrors.address2}</div>}
              <input
                type="text"
                name="address2"
                id="checkout-address2"
                placeholder="Barangay / Apartment, Suite, etc."
                value={formData.address2 || ''}
                onChange={handleChange}
                autoComplete="address-line2"
              />
            </div>
            )}
            {shippingMethod !== 'pickup' && (
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                {checkoutErrors.postalCode && <div className="field-error">{checkoutErrors.postalCode}</div>}
                <input
                  type="text"
                  name="postalCode"
                  id="checkout-postalCode"
                  placeholder="Postal code"
                  value={formData.postalCode}
                  onChange={handleChange}
                  autoComplete="postal-code"
                />
              </div>
              <div style={{ flex: 1 }}>
                {checkoutErrors.city && <div className="field-error">{checkoutErrors.city}</div>}
                <select name="city" id="checkout-city" value={formData.city} onChange={handleChange} disabled={!formData.region}>
                  <option value="">{formData.region ? 'Select city' : 'Select province first'}</option>
                  {(provincesMap[formData.region] || []).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            )}
            {shippingMethod !== 'pickup' && (
            <div>
              {checkoutErrors.region && <div className="field-error">{checkoutErrors.region}</div>}
              <select name="region" id="checkout-region" value={formData.region} onChange={handleChange} autoComplete="address-level1" disabled={loadingProvinces}>
                <option value="">{loadingProvinces ? 'Loading provinces...' : 'Please select your province'}</option>
                {Object.keys(provincesMap).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            )}
            
            {shippingMethod !== 'pickup' && (
            <input
              type="text"
              name="landmark"
              id="checkout-landmark"
              placeholder="Landmark (Optional - e.g., Near Jollibee)"
              value={formData.landmark}
              onChange={handleChange}
            />
            )}

            {shippingMethod !== 'pickup' && (
            <textarea
              name="deliveryNotes"
              id="checkout-deliveryNotes"
              placeholder="Delivery Notes (Optional - e.g., Gate code, call upon arrival)"
              value={formData.deliveryNotes}
              onChange={handleChange}
              rows="3"
              style={{ resize: 'vertical', fontFamily: 'inherit', padding: '12px', fontSize: '14px', width: '100%', borderRadius: '8px', border: '1px solid #ddd' }}
            />
            )}

            {/* Save as default address checkbox */}
            {useNewAddress && shippingMethod !== 'pickup' && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                padding: '1rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                marginTop: '0.5rem'
              }}>
                <input
                  type="checkbox"
                  id="saveAsDefault"
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  style={{ 
                    width: '18px', 
                    height: '18px',
                    minWidth: '18px',
                    minHeight: '18px',
                    cursor: 'pointer',
                    accentColor: '#7ba93a',
                    outline: 'none',
                    margin: 0,
                    flexShrink: 0
                  }}
                />
                <label 
                  htmlFor="saveAsDefault" 
                  style={{ 
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    color: '#333',
                    fontWeight: 500,
                    userSelect: 'none',
                    lineHeight: '18px',
                    margin: 0
                  }}
                >
                  💾 Save this as my default address
                </label>
              </div>
            )}

            {/* Delivery Availability Error */}
            {checkoutErrors.delivery && (
              <div style={{ 
                padding: '1rem', 
                background: '#fee', 
                border: '2px solid #d32f2f', 
                borderRadius: '8px', 
                color: '#d32f2f', 
                fontWeight: 600,
                marginTop: '1rem'
              }}>
                ⚠️ {checkoutErrors.delivery}
              </div>
            )}

            {/* Mobile Rewards & Promotions - OUTSIDE order summary dropdown */}
            <div className="mobile-rewards-promotions-wrapper" style={{ display: isMobile ? 'block' : 'none' }}>
              {/* Admin Rewards Selection for Mobile */}
              {availableRewards.length > 0 && (
                <div style={{ marginBottom: '1rem', marginTop: '1.5rem', background: '#fff', border: '1px solid #ececec', borderRadius: '16px', padding: '1.5rem 1.3rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <h4 style={{ margin: 0, color: '#175c36', fontSize: '1.1rem', fontWeight: 600 }}>Available Rewards</h4>
                    {selectedReward && (
                      <button
                        onClick={() => {
                          setSelectedReward(null);
                          setRewardError('');
                        }}
                        style={{
                          padding: '0.4rem 0.8rem',
                          fontSize: '0.85rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 500
                        }}
                      >
                        Unselect
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '0.5rem' }}>
                    {availableRewards.map((reward) => {
                      const today = new Date();
                      const exp = reward.expirationDate ? new Date(reward.expirationDate) : null;
                      const eligible = reward.active && (!reward.minCartAmount || subtotal >= reward.minCartAmount) && (!exp || exp >= today);
                      const hasEnoughPoints = (userPoints || 0) >= (reward.pointCost || 0);
                      return (
                        <div
                          key={reward._id}
                          onClick={() => eligible && hasEnoughPoints && handleRewardSelect(reward)}
                          style={{
                            padding: '0.75rem',
                            border: `2px solid ${selectedReward && selectedReward._id === reward._id ? '#175c36' : '#f0f0f0'}`,
                            borderRadius: '8px',
                            margin: '0.25rem 0',
                            cursor: eligible && hasEnoughPoints ? 'pointer' : 'not-allowed',
                            backgroundColor: selectedReward && selectedReward._id === reward._id ? '#f8f9fa' : 'white',
                            opacity: eligible && hasEnoughPoints ? 1 : 0.5,
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <div style={{ fontWeight: '600', color: '#175c36', fontSize: '0.9rem' }}>
                            {reward.name || reward.description}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                            {reward.type === 'cart' ? 'Cart Discount' : 'Delivery Discount'}
                            {' | '}
                            {reward.valueType === 'percent' ? `${reward.value}% off` : `₱${reward.value} off`}
                            {reward.minCartAmount ? ` | Min Cart: ₱${reward.minCartAmount}` : ''}
                            {reward.pointCost ? ` | Cost: ${reward.pointCost} pts` : ''}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                            Expires: {reward.expirationDate ? new Date(reward.expirationDate).toLocaleDateString() : 'No Expiry'}
                          </div>
                          {!eligible && <div style={{ color: 'red', fontSize: '0.8rem', marginTop: '0.25rem' }}>Not eligible</div>}
                          {!hasEnoughPoints && <div style={{ color: 'red', fontSize: '0.8rem', marginTop: '0.25rem' }}>Not enough points</div>}
                        </div>
                      );
                    })}
                  </div>
                  {rewardError && <div style={{ color: 'red', marginTop: 4 }}>{rewardError}</div>}
                </div>
              )}

              {/* Admin Promotions for Mobile */}
              {availablePromotions.length > 0 && (
                <div style={{ marginBottom: '1rem', background: '#fff', border: '1px solid #ececec', borderRadius: '16px', padding: '1.5rem 1.3rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#175c36', fontSize: '1.1rem', fontWeight: 600 }}>Available Promo Codes</h4>
                  <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '0.5rem' }}>
                    {availablePromotions.map((promo) => (
                      <div
                        key={promo._id}
                        style={{
                          padding: '0.75rem',
                          border: '2px solid #f0f0f0',
                          borderRadius: '8px',
                          margin: '0.25rem 0',
                          backgroundColor: 'white',
                          transition: 'all 0.3s ease',
                          cursor: promo.code ? 'pointer' : 'default'
                        }}
                        onClick={() => promo.code && handleApplyPromo(promo.code)}
                      >
                        <div style={{ fontWeight: '600', color: '#175c36', fontSize: '0.9rem' }}>
                          {promo.code || (promo.description || `${promo.value ?? promo.amount ?? ''}`)}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                          {(promo.discountType || promo.type) === '%' ? `${promo.value ?? promo.amount}% off` : ((promo.value ?? promo.amount) ? `₱${promo.value ?? promo.amount}` : promo.description || '')}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                          Valid: {promo.validFrom ? new Date(promo.validFrom).toLocaleDateString() : '—'} to {promo.validTo ? new Date(promo.validTo).toLocaleDateString() : '—'}
                          {promo.minCartAmount ? ` | Min Cart: ₱${promo.minCartAmount}` : ''}
                          {promo.perUserLimit ? ` | Limit per user: ${promo.perUserLimit}` : ' | Unlimited uses'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Promo Code input for Mobile */}
              <div style={{ marginBottom: '1.5rem', background: '#fff', border: '1px solid #ececec', borderRadius: '16px', padding: '1.5rem 1.3rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <h4 style={{ margin: '0 0 0.75rem 0', color: '#175c36', fontSize: '1.1rem', fontWeight: 600 }}>Promo Code</h4>
                <div style={{ display: 'flex', alignItems: 'center', background: '#f5f5f5', borderRadius: '24px', padding: '0.3rem 0.7rem', border: '1px solid #e0e0e0' }}>
                  <span style={{ marginRight: 8, color: '#888', fontSize: '1.1rem', display: 'flex', alignItems: 'center' }}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="7" width="14" height="6" rx="3" fill="#bbb"/><circle cx="7" cy="10" r="1" fill="#fff"/><circle cx="13" cy="10" r="1" fill="#fff"/></svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={e => setPromoCode(e.target.value)}
                    style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: '1rem', color: '#444', padding: '0.4rem 0' }}
                  />
                  <button style={{ background: '#7ba93a', color: '#fff', border: 'none', borderRadius: '24px', padding: '0.5rem 1.5rem', fontWeight: 600, fontSize: '1rem', marginLeft: 8, cursor: 'pointer', boxShadow: '0 1px 4px rgba(123,169,58,0.08)' }} onClick={() => handleApplyPromo()}>Apply</button>
                </div>
                {promoError && <div style={{ color: 'red', marginTop: 8, fontSize: '0.85rem' }}>{promoError}</div>}
              </div>
            </div>

            {/* Mobile Order Summary Toggle - appears before Payment */}
            <div className="mobile-summary-toggle-wrapper">
              <button 
                className={`order-summary-toggle ${showOrderSummary ? 'open' : ''}`}
                onClick={() => setShowOrderSummary(!showOrderSummary)}
              >
                {showOrderSummary ? 'Hide' : 'Show'} Order Summary
                <span className="toggle-icon">{showOrderSummary ? '▲' : '▼'}</span>
              </button>

              {/* Mobile Order Summary Content */}
              {showOrderSummary && (
                <div className="mobile-order-summary" style={{ marginTop: '1rem', background: '#fff', border: '1px solid #ececec', borderRadius: '16px', padding: '1.5rem 1.3rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontWeight: 600, fontSize: '1.18rem', marginBottom: '1.1rem' }}>Order Summary</div>
                  
                  {/* Items list, scrollable inside card */}
                  <div style={{ maxHeight: '340px', overflowY: 'auto', marginBottom: '1.1rem', background: '#f8f8f8', borderRadius: '10px', padding: '0.5rem 1rem', width: '100%' }}>
                    {cart.length === 0 ? (
                      <div style={{ color: '#888', textAlign: 'center', padding: '1.2rem 0' }}>No items in cart.</div>
                    ) : (
                      cart.map(item => (
                        <div key={item.product?._id || item._id} style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', marginBottom: '1.1rem', borderBottom: '1px solid #ececec', paddingBottom: '0.7rem', width: '100%' }}>
                          <img
                            src={item.product?.image || '/default-plant.png'}
                            alt={item.product?.name || 'Unknown'}
                            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, marginRight: 12, border: '1px solid #bbb' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{item.product?.name || 'Unknown'}</div>
                          </div>
                          <div style={{ fontWeight: 500, fontSize: '1rem', color: '#444', marginRight: 12 }}>x{item.quantity}</div>
                          <div style={{ fontWeight: 600, fontSize: '1.05rem', minWidth: 70, textAlign: 'right' }}>
                            {item.product?.discount && Number(item.product.discount) > 0 ? (
                              <>
                                <span style={{ textDecoration: 'line-through', color: '#888', fontWeight: 500, marginRight: 6 }}>
                                  ₱{(Number(item.product.price) * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                                <span style={{ color: '#d32f2f', fontWeight: 700 }}>
                                  ₱{((Number(item.product.price) - (Number(item.product.price) * (Number(item.product.discount) / 100))) * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                              </>
                            ) : (
                              <>₱{((item.product?.price || 0) * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
                    <span style={{ color: '#444', fontWeight: 500 }}>Subtotal</span>
                    <span style={{ fontWeight: 600 }}>₱{subtotal.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
                    <span style={{ color: '#444', fontWeight: 500 }}>Delivery Fee</span>
                    <span style={{ fontWeight: 600 }}>{deliveryFee === 0 ? 'FREE' : `₱${deliveryFee.toFixed(2)}`}</span>
                  </div>
                  
                  {/* Discount row for mobile */}
                  {promoApplied && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.7rem', color: '#7ba93a', fontWeight: 600 }}>
                      <span>Discount</span>
                      <span>
                        {discountType === '%'
                          ? `-₱${((subtotal * discount) / 100).toFixed(2)} (${discount}%)`
                          : `-₱${discount.toFixed(2)}`}
                      </span>
                    </div>
                  )}
                  
                  {/* Reward discount for mobile */}
                  {selectedReward && selectedReward.type === 'cart' && selectedReward.active && (() => {
                    const today = new Date();
                    const exp = selectedReward.expirationDate ? new Date(selectedReward.expirationDate) : null;
                    if ((!selectedReward.minCartAmount || subtotal >= selectedReward.minCartAmount) && (!exp || exp >= today)) {
                      return (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.7rem', color: '#175c36', fontWeight: 600 }}>
                          <span>Reward Discount</span>
                          <span>
                            {selectedReward.valueType === 'percent'
                              ? `-₱${((subtotal * selectedReward.value) / 100).toFixed(2)} (${selectedReward.value}%)`
                              : `-₱${selectedReward.value.toFixed(2)}`}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <hr style={{ border: 'none', borderTop: '1px solid #ececec', margin: '1.1rem 0 1.1rem 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
                    <span style={{ fontWeight: 700, fontSize: '1.15rem' }}>Total</span>
                    <span style={{ fontWeight: 700, fontSize: '1.15rem', color: '#222' }}>
                      ₱{(() => {
                        let total = subtotal;
                        if (promoApplied) {
                          total -= (discountType === '%' ? (subtotal * discount) / 100 : discount);
                        }
                        if (selectedReward && selectedReward.type === 'cart' && selectedReward.active) {
                          const today = new Date();
                          const exp = selectedReward.expirationDate ? new Date(selectedReward.expirationDate) : null;
                          if ((!selectedReward.minCartAmount || subtotal >= selectedReward.minCartAmount) && (!exp || exp >= today)) {
                            if (selectedReward.valueType === 'flat') {
                              total -= selectedReward.value;
                            } else if (selectedReward.valueType === 'percent') {
                              total -= (subtotal * selectedReward.value) / 100;
                            }
                          }
                        }
                        total += deliveryFee;
                        return total.toFixed(2);
                      })()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Section */}
            <h2 style={{ margin: '2rem 0 0.7rem 0' }}>Payment</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Online Payment (PayMongo) */}
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '1.25rem 1rem', 
                border: formData.paymentMethod === 'online' ? '2px solid #7ba93a' : '2px solid #e0e0e0', 
                borderRadius: '12px', 
                cursor: 'pointer',
                background: formData.paymentMethod === 'online' ? '#f0f7ed' : 'white',
                transition: 'all 0.3s ease',
                boxShadow: formData.paymentMethod === 'online' ? '0 4px 12px rgba(123,169,58,0.15)' : '0 2px 6px rgba(0,0,0,0.05)',
                transform: formData.paymentMethod === 'online' ? 'translateY(-2px)' : 'none'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  border: formData.paymentMethod === 'online' ? '2px solid #7ba93a' : '2px solid #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '1rem',
                  background: formData.paymentMethod === 'online' ? '#7ba93a' : 'white',
                  transition: 'all 0.3s ease'
                }}>
                  {formData.paymentMethod === 'online' && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'white' }} />}
                </div>
                <input 
                  type="radio" 
                  name="paymentMethod" 
                  value="online" 
                  checked={formData.paymentMethod === 'online'}
                  onChange={handleChange}
                  style={{ position: 'absolute', opacity: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '1.05rem', color: formData.paymentMethod === 'online' ? '#175c36' : '#333' }}>
                    💳 Online Payment (via PayMongo)
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                    GCash, Credit/Debit Card, PayMaya, and more
                  </div>
                </div>
              </label>

              {/* Cash on Delivery */}
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '1.25rem 1rem', 
                border: formData.paymentMethod === 'cod' ? '2px solid #7ba93a' : '2px solid #e0e0e0', 
                borderRadius: '12px', 
                cursor: 'pointer',
                background: formData.paymentMethod === 'cod' ? '#f0f7ed' : 'white',
                transition: 'all 0.3s ease',
                boxShadow: formData.paymentMethod === 'cod' ? '0 4px 12px rgba(123,169,58,0.15)' : '0 2px 6px rgba(0,0,0,0.05)',
                transform: formData.paymentMethod === 'cod' ? 'translateY(-2px)' : 'none'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  border: formData.paymentMethod === 'cod' ? '2px solid #7ba93a' : '2px solid #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '1rem',
                  background: formData.paymentMethod === 'cod' ? '#7ba93a' : 'white',
                  transition: 'all 0.3s ease'
                }}>
                  {formData.paymentMethod === 'cod' && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'white' }} />}
                </div>
                <input 
                  type="radio" 
                  name="paymentMethod" 
                  value="cod" 
                  checked={formData.paymentMethod === 'cod'}
                  onChange={handleChange}
                  style={{ position: 'absolute', opacity: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '1.05rem', color: formData.paymentMethod === 'cod' ? '#175c36' : '#333' }}>
                    💵 Cash on Delivery
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>Pay when you receive your order</div>
                </div>
              </label>
            </div>

            <button style={{ width: '100%', marginTop: '2rem', background: '#7ba93a', color: '#fff', borderRadius: '24px', fontWeight: 600, fontSize: '1.1rem', padding: '16px 0', border: 'none', boxShadow: '0 2px 8px rgba(122,156,58,0.08)' }} onClick={handlePayNow}>
              Pay now &rarr;
            </button>
          </div>

          {/* RIGHT COLUMN */}
          <div className={`checkout-right ${showOrderSummary ? 'show-summary' : ''}`}>
            <div style={{ marginTop: '0.5rem', background: '#fff', border: '1px solid #ececec', borderRadius: '16px', padding: '1.5rem 1.3rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ fontWeight: 600, fontSize: '1.18rem', marginBottom: '1.1rem' }}>Order Summary</div>
              {/* Items list, scrollable inside card */}
              <div style={{ maxHeight: '340px', overflowY: 'auto', marginBottom: '1.1rem', background: '#f8f8f8', borderRadius: '10px', padding: '0.5rem 1rem', width: '100%' }}>
                {cart.length === 0 ? (
                  <div style={{ color: '#888', textAlign: 'center', padding: '1.2rem 0' }}>No items in cart.</div>
                ) : (
                  cart.map(item => (
                    <div key={item.product?._id || item._id} style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', marginBottom: '1.1rem', borderBottom: '1px solid #ececec', paddingBottom: '0.7rem', width: '100%' }}>
                      <img
                        src={item.product?.image || '/default-plant.png'}
                        alt={item.product?.name || 'Unknown'}
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, marginRight: 12, border: '1px solid #bbb' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{item.product?.name || 'Unknown'}</div>
                      </div>
                      <div style={{ fontWeight: 500, fontSize: '1rem', color: '#444', marginRight: 12 }}>x{item.quantity}</div>
                      <div style={{ fontWeight: 600, fontSize: '1.05rem', minWidth: 70, textAlign: 'right' }}>
                        {item.product?.discount && Number(item.product.discount) > 0 ? (
                          <>
                            <span style={{ textDecoration: 'line-through', color: '#888', fontWeight: 500, marginRight: 6 }}>
                              ₱{(Number(item.product.price) * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </span>
                            <span style={{ color: '#d32f2f', fontWeight: 700 }}>
                              ₱{((Number(item.product.price) - (Number(item.product.price) * (Number(item.product.discount) / 100))) * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </span>
                          </>
                        ) : (
                          <>₱{((item.product?.price || 0) * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              {/* ...existing summary rows... */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
                <span style={{ color: '#444', fontWeight: 500 }}>Subtotal</span>
                <span style={{ fontWeight: 600 }}>₱{subtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
                <span style={{ color: '#444', fontWeight: 500 }}>Delivery Fee</span>
                <span style={{ fontWeight: 600 }}>{deliveryFee === 0 ? 'FREE' : `₱${deliveryFee.toFixed(2)}`}</span>
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid #ececec', margin: '1.1rem 0 1.1rem 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
                <span style={{ fontWeight: 700, fontSize: '1.15rem' }}>Total</span>
                <span style={{ fontWeight: 700, fontSize: '1.15rem', color: '#222' }}>
                  ₱{(() => {
                    let total = subtotal;
                    // Promo discount
                    if (promoApplied) {
                      total -= (discountType === '%' ? (subtotal * discount) / 100 : discount);
                    }
                    // Admin reward (cart type)
                    if (selectedReward && selectedReward.type === 'cart' && selectedReward.active) {
                      const today = new Date();
                      const exp = selectedReward.expirationDate ? new Date(selectedReward.expirationDate) : null;
                      if ((!selectedReward.minCartAmount || subtotal >= selectedReward.minCartAmount) && (!exp || exp >= today)) {
                        if (selectedReward.valueType === 'flat') {
                          total -= selectedReward.value;
                        } else if (selectedReward.valueType === 'percent') {
                          total -= (subtotal * selectedReward.value) / 100;
                        }
                      }
                    }
                    // Delivery fee
                    total += deliveryFee;
                    return total.toFixed(2);
                  })()}
                </span>
              </div>
              {/* Admin Rewards Selection */}
              {availableRewards.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, color: '#175c36', fontSize: '1rem' }}>Available Rewards</h4>
                    {selectedReward && (
                      <button
                        onClick={() => {
                          setSelectedReward(null);
                          setRewardError('');
                        }}
                        style={{
                          padding: '0.35rem 0.7rem',
                          fontSize: '0.8rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 500
                        }}
                      >
                        Unselect
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '0.5rem' }}>
                    {availableRewards.map((reward) => {
                      const today = new Date();
                      const exp = reward.expirationDate ? new Date(reward.expirationDate) : null;
                      const eligible = reward.active && (!reward.minCartAmount || subtotal >= reward.minCartAmount) && (!exp || exp >= today);
                      const hasEnoughPoints = (userPoints || 0) >= (reward.pointCost || 0);
                      return (
                        <div
                          key={reward._id}
                          onClick={() => eligible && hasEnoughPoints && handleRewardSelect(reward)}
                          style={{
                            padding: '0.75rem',
                            border: `2px solid ${selectedReward && selectedReward._id === reward._id ? '#175c36' : '#f0f0f0'}`,
                            borderRadius: '8px',
                            margin: '0.25rem 0',
                            cursor: eligible && hasEnoughPoints ? 'pointer' : 'not-allowed',
                            backgroundColor: selectedReward && selectedReward._id === reward._id ? '#f8f9fa' : 'white',
                            opacity: eligible && hasEnoughPoints ? 1 : 0.5,
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <div style={{ fontWeight: '600', color: '#175c36', fontSize: '0.9rem' }}>
                            {reward.name || reward.description}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                            {reward.type === 'cart' ? 'Cart Discount' : 'Delivery Discount'}
                            {' | '}
                            {reward.valueType === 'percent' ? `${reward.value}% off` : `₱${reward.value} off`}
                            {reward.minCartAmount ? ` | Min Cart: ₱${reward.minCartAmount}` : ''}
                            {reward.pointCost ? ` | Cost: ${reward.pointCost} pts` : ''}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                            Expires: {reward.expirationDate ? new Date(reward.expirationDate).toLocaleDateString() : 'No Expiry'}
                          </div>
                          {!eligible && <div style={{ color: 'red', fontSize: '0.8rem', marginTop: '0.25rem' }}>Not eligible</div>}
                          {!hasEnoughPoints && <div style={{ color: 'red', fontSize: '0.8rem', marginTop: '0.25rem' }}>Not enough points</div>}
                        </div>
                      );
                    })}
                  </div>
                  {rewardError && <div style={{ color: 'red', marginTop: 4 }}>{rewardError}</div>}
                </div>
              )}
              

              {/* Admin Promotions (shown as vouchers that can be applied) */}
              {availablePromotions.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#175c36', fontSize: '1rem' }}>Available Promo Codes</h4>
                  <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '0.5rem' }}>
                    {availablePromotions.map((promo) => (
                      <div
                        key={promo._id}
                        style={{
                          padding: '0.75rem',
                          border: '2px solid #f0f0f0',
                          borderRadius: '8px',
                          margin: '0.25rem 0',
                          backgroundColor: 'white',
                          transition: 'all 0.3s ease',
                          cursor: promo.code ? 'pointer' : 'default'
                        }}
                        onClick={() => promo.code && handleApplyPromo(promo.code)}
                      >
                        <div style={{ fontWeight: '600', color: '#175c36', fontSize: '0.9rem' }}>
                          {promo.code || (promo.description || `${promo.value ?? promo.amount ?? ''}`)}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                          {(promo.discountType || promo.type) === '%' ? `${promo.value ?? promo.amount}% off` : ((promo.value ?? promo.amount) ? `₱${promo.value ?? promo.amount}` : promo.description || '')}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
              Valid: {promo.validFrom ? new Date(promo.validFrom).toLocaleDateString() : '—'} to {promo.validTo ? new Date(promo.validTo).toLocaleDateString() : '—'}
              {promo.minCartAmount ? ` | Min Cart: ₱${promo.minCartAmount}` : ''}
              {promo.perUserLimit ? ` | Limit per user: ${promo.perUserLimit}` : ' | Unlimited uses'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* User vouchers removed (redundant / not in use) */}
              {/* Promo Code input and button styled like the image */}
              <div style={{ display: 'flex', alignItems: 'center', background: '#f5f5f5', borderRadius: '24px', padding: '0.3rem 0.7rem', marginBottom: '0.7rem', border: '1px solid #e0e0e0' }}>
                <span style={{ marginRight: 8, color: '#888', fontSize: '1.1rem', display: 'flex', alignItems: 'center' }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="7" width="14" height="6" rx="3" fill="#bbb"/><circle cx="7" cy="10" r="1" fill="#fff"/><circle cx="13" cy="10" r="1" fill="#fff"/></svg>
                </span>
                <input
                  type="text"
                  placeholder="Add promo code"
                  value={promoCode}
                  onChange={e => setPromoCode(e.target.value)}
                  style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: '1rem', color: '#444', padding: '0.4rem 0' }}
                />
                <button style={{ background: '#7ba93a', color: '#fff', border: 'none', borderRadius: '24px', padding: '0.5rem 1.5rem', fontWeight: 600, fontSize: '1rem', marginLeft: 8, cursor: 'pointer', boxShadow: '0 1px 4px rgba(123,169,58,0.08)' }} onClick={() => handleApplyPromo()}>Apply</button>
              </div>
              {promoError && <div style={{ color: 'red', marginTop: 4 }}>{promoError}</div>}
              {/* Discount row, only show if applied */}
              {promoApplied && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.7rem', color: '#7ba93a', fontWeight: 600 }}>
                  <span>Discount</span>
                  <span>
                    {discountType === '%'
                      ? `-₱${((subtotal * discount) / 100).toFixed(2)} (${discount}%)`
                      : `-₱${discount.toFixed(2)}`}
                  </span>
                </div>
              )}
              {/* Voucher discount removed (vouchers handled elsewhere) */}
              {/* Admin reward discount row (cart type) */}
              {selectedReward && selectedReward.type === 'cart' && selectedReward.active && (() => {
                const today = new Date();
                const exp = selectedReward.expirationDate ? new Date(selectedReward.expirationDate) : null;
                if ((!selectedReward.minCartAmount || subtotal >= selectedReward.minCartAmount) && (!exp || exp >= today)) {
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.7rem', color: '#175c36', fontWeight: 600 }}>
                      <span>Reward Discount</span>
                      <span>
                        {selectedReward.valueType === 'percent'
                          ? `-₱${((subtotal * selectedReward.value) / 100).toFixed(2)} (${selectedReward.value}%)`
                          : `-₱${selectedReward.value.toFixed(2)}`}
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
              {/* Free shipping voucher message removed */}
              {/* Admin reward delivery message */}
              {selectedReward && selectedReward.type === 'delivery' && selectedReward.active && (() => {
                const today = new Date();
                const exp = selectedReward.expirationDate ? new Date(selectedReward.expirationDate) : null;
                if ((!selectedReward.minCartAmount || subtotal >= selectedReward.minCartAmount) && (!exp || exp >= today)) {
                  return (
                    <div style={{ color: '#175c36', fontWeight: 'bold', marginTop: 4 }}>
                      Delivery reward applied!
                    </div>
                  );
                }
                return null;
              })()}
              {/* Free delivery message */}
              {subtotal >= 10000 && (
                <div style={{ color: '#7ba93a', fontWeight: 'bold', marginTop: 4 }}>Free delivery applied!</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <div style={{ 
        width: '100%', 
        background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', 
        minHeight: 80, 
        display: 'flex', 
        flexDirection: 'column',
        gap: '12px',
        alignItems: 'center', 
        justifyContent: 'center', 
        marginTop: isMobile ? '40px' : '60px',
        padding: '20px 16px',
        boxShadow: '0 -2px 8px rgba(23, 92, 54, 0.1)'
      }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <a href="https://www.facebook.com/profile.php?id=61552636677232" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.9rem', opacity: 0.9, transition: 'opacity 0.3s' }} onMouseEnter={(e) => e.target.style.opacity = 1} onMouseLeave={(e) => e.target.style.opacity = 0.9}>Facebook</a>
          <span style={{ color: 'rgba(255,255,255,0.5)' }}>•</span>
          <a href="https://www.messenger.com/t/61552636677232" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.9rem', opacity: 0.9, transition: 'opacity 0.3s' }} onMouseEnter={(e) => e.target.style.opacity = 1} onMouseLeave={(e) => e.target.style.opacity = 0.9}>Messenger</a>
          <span style={{ color: 'rgba(255,255,255,0.5)' }}>•</span>
          <a href="https://api.whatsapp.com/send?phone=%2B639368983053" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.9rem', opacity: 0.9, transition: 'opacity 0.3s' }} onMouseEnter={(e) => e.target.style.opacity = 1} onMouseLeave={(e) => e.target.style.opacity = 0.9}>WhatsApp</a>
          <span style={{ color: 'rgba(255,255,255,0.5)' }}>•</span>
          <a href="/return-policy" style={{ color: '#fff', textDecoration: 'none', fontSize: '0.9rem', opacity: 0.9, transition: 'opacity 0.3s' }} onMouseEnter={(e) => e.target.style.opacity = 1} onMouseLeave={(e) => e.target.style.opacity = 0.9}>Return & Exchange Policy</a>
        </div>
        <span style={{ 
          color: '#fff', 
          fontSize: isMobile ? '0.85rem' : '0.9rem', 
          fontWeight: 500, 
          letterSpacing: 0.5,
          opacity: 0.9
        }}>
          © {new Date().getFullYear()} Chiqui's Garden
        </span>
      </div>
    </div>
  );
};

export default CheckoutPage;
